"use client";
import { HoursPanel } from "@/features/reserve/components/ResourceSettingsPanel/HoursPanel";
import { GeneralTab } from "@/features/reserve/components/resources/tabs/GeneralTab";
import { GeneralTabMultiple } from "@/features/reserve/components/resources/tabs/GeneralTabMultiple";
import { RestrictionsTab } from "@/features/reserve/components/resources/tabs/access-restrictions/RestrictionsTab";
import { cn } from "@gettactic/helpers/src/css/cn";
import { IResource } from "@gettactic/api";
import { useAuthenticated } from "@gettactic/hooks";
import { useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from "@cmp-src/primitives/tabs";

type Props = {
  resources: IResource[];
  notifyUpdateLabels?: (resWithLabels: IResource[]) => void;
  onSaved?: () => void;
};

export function ResourcePanel({
  resources,
  notifyUpdateLabels,
  onSaved
}: Props) {
  const {
    userContext: { currentOffice }
  } = useAuthenticated();
  const queryClient = useQueryClient();
  const isSingle = resources.length === 1;
  const afterEdit = async () => {
    queryClient.invalidateQueries({
      queryKey: ["organizations.resourcesAll", currentOffice.id]
    });
  };

  const tabs: {
    id: string;
    title: string;
    condition: boolean;
    render: () => React.ReactNode | null;
  }[] = [
    {
      id: "general",
      title: "General",
      condition: true,
      render: () =>
        isSingle ? (
          <GeneralTab
            mutate={afterEdit}
            resource={resources[0]}
            onSaved={onSaved}
          />
        ) : (
          <GeneralTabMultiple
            notifyUpdateLabels={notifyUpdateLabels}
            mutate={afterEdit}
            resources={resources}
            key={resources.map((x) => x.id).join(",")}
            onSaved={onSaved}
          />
        )
    },
    {
      id: "hours",
      title: "Hours",
      condition: true,
      render: () => <HoursPanel resources={resources} office={currentOffice} />
    },
    {
      id: "restrictions",
      title: "Assignments",
      condition: true,
      render: () => (
        <RestrictionsTab
          mutateAll={afterEdit}
          resources={resources}
          key={resources.map((x) => x.id).join(",")}
        />
      )
    }
  ];

  const visibleTabs = tabs.filter((tabDef) => tabDef.condition);
  const [selectedTab, setSelectedTab] = useState(visibleTabs[0]?.id || "");
  return (
    <Tabs
      value={selectedTab}
      onValueChange={setSelectedTab}
      defaultValue={visibleTabs[0]?.id}
      className="contents"
    >
      <TabsList
        aria-label="Permission tabs"
        className="col-span-12 flex flex-none gap-8 border-b border-neutral-200 text-base"
      >
        {visibleTabs.map((tabDef) => (
          <TabsTrigger
            key={tabDef.id}
            value={tabDef.id}
            className={cn(
              "whitespace-nowrap border-b-2 px-1 py-4 font-medium focus:border-primary focus:outline-none focus:ring-primary",
              "border-transparent text-neutral-800 hover:border-neutral-300 hover:text-neutral-600",
              "data-[state=active]:border-primary data-[state=active]:text-primary"
            )}
            type="button"
          >
            {tabDef.title}
          </TabsTrigger>
        ))}
      </TabsList>
      {visibleTabs.map((tabDef) => (
        <TabsContent
          key={tabDef.id}
          value={tabDef.id}
          className="col-span-12 flex h-full flex-auto flex-col flex-nowrap overflow-hidden focus:border-primary focus:outline-none focus:ring-primary"
        >
          {selectedTab === tabDef.id ? tabDef.render() : null}
        </TabsContent>
      ))}
    </Tabs>
  );
}
